// All-WebGL streaming pipeline — P3c (STREAM_PIPELINE.md).
//
// The UV→3D lift as a spark `SplatGenerator`, entirely in spark's WebGL context
// — so the lifted splats never leave the GPU (P0 proved spark sorts generated
// splats with a key-only readback). Two stages share the proven GLSL of
// `webgl/liftGlsl.ts`:
//   pass1 — a three-native MRT render pass (RawShaderMaterial GLSL3, PASS1_FS)
//           into a 3-target float WebGLRenderTarget. Re-run only on a new pose.
//   pass2 — the generator dyno: per `index` = sample s, `liftSample(...)` reads
//           the pass1 targets + valid/color textures, eigendecomposes, culls
//           (flags=0u), and emits the gsplat. Identical math to the raw-GL
//           reference oracle + its parity test, which live in the donor research
//           repo (not ported here).
//
// Spark-boundary conventions (from lift_pass2.wgsl's packed path): quaternion is
// reordered (w,x,y,z)→(x,y,z,w) for spark's PackedSplats, and opacity is
// sigmoid(logit) (the lift emits a logit; spark's rgba.a wants [0,1]).

import * as THREE from "three";
import { SplatGenerator, dyno } from "@sparkjsdev/spark";
import { PASS1_FS, FULLSCREEN_VS, LIFT_SAMPLE_GLSL, SH_EVAL_BILINEAR_GLSL } from "../webgl/liftGlsl";
import type { LiftDims } from "../webgl/liftTypes";
import { ThreeUntile } from "./ThreeUntile";
import type { UntileMeta } from "../webgl/WebGlUntile";

import { scaleMapConstants, type ScaleMapConstants } from "../../inference/splatScale.ts";
const D = dyno as any;
const { dynoBlock, Dyno, Gsplat, combineGsplat, dynoConst, dynoSampler2D, dynoVec3 } = D;

const TW = 2048; // data-texture + pass1 target width (matches WebGlLift)

function dataTex(len: number): THREE.DataTexture {
  const w = Math.min(TW, Math.max(1, len));
  const h = Math.max(1, Math.ceil(len / w));
  const t = new THREE.DataTexture(new Float32Array(w * h), w, h, THREE.RedFormat, THREE.FloatType);
  t.minFilter = THREE.NearestFilter; t.magFilter = THREE.NearestFilter;
  t.wrapS = THREE.ClampToEdgeWrapping; t.wrapT = THREE.ClampToEdgeWrapping;
  t.generateMipmaps = false; t.flipY = false; t.needsUpdate = true;
  (t as any)._tw = w;
  return t;
}
function writeTex(t: THREE.DataTexture, data: ArrayLike<number>): void {
  (t.image.data as unknown as Float32Array).set(data as unknown as Float32Array, 0);
  t.needsUpdate = true;
}

// Raw lift dyno: index (+ pass1/valid/color samplers + dims) → gsplat fields.
// Reuses LIFT_SAMPLE_GLSL verbatim (the parity-gated pass2 core). When `sh` is
// true, color is a view-dependent SH eval (P4b) from the KC-channel coeff
// colorTex using the bilinear neighbours liftSample exposes, with a per-frame
// `camPos`. With `compact`, index is remapped through a static sample-map
// texture (valid samples only) so dead grid slots never reach the spark sort.
function liftDyno(args: Record<string, any>, sh: boolean, compact: boolean) {
  const inTypes: Record<string, string> = {
    index: "int",
    o0: "sampler2D", o1: "sampler2D", o2: "sampler2D",
    validTex: "sampler2D", colorTex: "sampler2D",
    H: "int", W: "int", N: "int", HW: "int", twP: "int", twV: "int", twC: "int",
  };
  const globals: string[] = [LIFT_SAMPLE_GLSL];
  if (sh) { inTypes.camPos = "vec3"; inTypes.K = "int"; globals.push(SH_EVAL_BILINEAR_GLSL); }
  if (compact) { inTypes.sampleTex = "sampler2D"; inTypes.twS = "int"; }
  const scaleCull = "scaleMax" in args;
  if (scaleCull) inTypes.scaleMax = "float";
  return new Dyno({
    inTypes,
    outTypes: {
      center: "vec3", scales: "vec3", quaternion: "vec4",
      rgb: "vec3", opacity: "float", flags: "uint",
    },
    inputs: args,
    globals: () => globals,
    statements: ({ inputs, outputs }: any) => [
      `vec3 _c, _s, _rgb; vec4 _q; float _op; ivec4 _nbr; vec4 _wgt;`,
      compact
        ? `int _smp = int(f1(${inputs.sampleTex}, ${inputs.index}, ${inputs.twS}));`
        : `int _smp = ${inputs.index};`,
      `bool _keep = liftSample(${inputs.o0}, ${inputs.o1}, ${inputs.o2}, ${inputs.validTex}, ${inputs.colorTex}, ` +
        `${inputs.H}, ${inputs.W}, ${inputs.N}, ${inputs.HW}, ${inputs.twP}, ${inputs.twV}, ${inputs.twC}, _smp, ` +
        `_c, _s, _q, _rgb, _op, _nbr, _wgt);`,
      // Over-stretch cull (render_macarena.py --cull_cov_pct analogue): drop
      // splats whose largest eigen-scale exceeds scaleMax metres.
      ...(scaleCull ? [`_keep = _keep && max(_s.x, max(_s.y, _s.z)) <= ${inputs.scaleMax};`] : []),
      `${outputs.center} = _c;`,
      `${outputs.scales} = _keep ? _s : vec3(0.0);`,         // zero-scale culled splats (belt+braces with flags)
      `${outputs.quaternion} = vec4(_q.y, _q.z, _q.w, _q.x);`, // (w,x,y,z) → (x,y,z,w) for spark
      sh
        ? `${outputs.rgb} = shEvalBilinear(${inputs.colorTex}, ${inputs.twC}, ${inputs.HW}, _nbr, _wgt, _c, ${inputs.camPos}, ${inputs.K});`
        : `${outputs.rgb} = _rgb;`,
      `${outputs.opacity} = 1.0 / (1.0 + exp(-_op));`,        // sigmoid(logit)
      `${outputs.flags} = _keep ? 1u : 0u;`,                 // GSPLAT_FLAG_ACTIVE = 1u
    ],
  });
}

export class StreamLiftGenerator {
  readonly generator: InstanceType<typeof SplatGenerator>;
  readonly numSplats: number;
  private renderer: THREE.WebGLRenderer;
  private d: LiftDims;

  // input textures
  private texIdxim: THREE.DataTexture;
  private texBarim: THREE.DataTexture;
  private texTriim: THREE.DataTexture;
  private texValid: THREE.DataTexture;
  private texVerts: THREE.DataTexture;
  private texJac: THREE.DataTexture;
  private texFaceValid: THREE.DataTexture;
  private texFaces: THREE.DataTexture | null = null;  // gpuJac: static F*3 vertex indices
  private texSampleMap: THREE.DataTexture | null = null; // compact mode: valid sample ids
  private scaleMax = 0;
  private gpuJac = false;
  /** Triangle-bounded clamp, mirroring the Python lift. `triKappa` > 0 projects
   *  each splat's world extent down to triKappa x its host triangle's bound;
   *  `sliverQMin` > 0 additionally zeroes splats on degenerate faces and derates
   *  the bound over [q, 5q]. Both 0 = OFF and byte-identical to before, so a
   *  bundle from a checkpoint trained without the flags is unaffected. These
   *  MUST match what the checkpoint was trained with -- the clamp is applied
   *  after the decoder, so the ONNX graph cannot carry it. */
  private triKappa = 0;
  private sliverQMin = 0;
  /** The trainer's scale map (inference/splatScale.ts), as pass-1 uniforms. */
  private scaleMap: ScaleMapConstants = scaleMapConstants(
    { sigmaMax: 0, sigmaOffset: 0, scaleLogBias: 0, scaleLogMax: Infinity });

  // pass1 fullscreen MRT render
  private rt: THREE.WebGLRenderTarget;
  private fsScene = new THREE.Scene();
  private fsCam = new THREE.Camera();
  private fsMat: THREE.RawShaderMaterial;
  private p1w: number; private p1h: number;
  /** Required: geom/color come from the WebGL untile (via runLift). pass1's geomTex
   *  + the dyno's colorTex point at its RTs. May be SHARED across branches
   *  (head+body) — only the owner disposes it. */
  private untile: ThreeUntile | null = null;
  private ownsUntile = false;
  private branch: "head" | "body" = "head";
  /** SH basis size (0 = 3-ch color path; 9 = deg-2, 16 = deg-3). When >0 the dyno
   *  evaluates view-dependent SH from the KC-channel colorTex (P4b). */
  private shK = 0;
  /** Per-frame camera position in object space (= world for identity transform),
   *  fed to the SH eval. Update each render via setCamera(). */
  private camPos = dynoVec3(new THREE.Vector3());

  constructor(
    renderer: THREE.WebGLRenderer,
    mesh: { idxim: ArrayLike<number>; barim: ArrayLike<number>; triim: ArrayLike<number>; valid: ArrayLike<number>; faces?: ArrayLike<number> },
    dims: LiftDims,
    untileSrc?: UntileMeta | ThreeUntile,
    shK = 0,
    branch: "head" | "body" = "head",
    gpuJac = false,
    sampleMap: ArrayLike<number> | null = null, // valid sample ids — compacts numSplats
    scaleMax = 0, // >0: cull splats whose largest eigen-scale exceeds this (metres)
    triKappa = 0,   // >0: clamp world extent to triKappa x the host triangle bound
    sliverQMin = 0, // >0: sliver faces host no splat; also sets the derate band
    // The trainer's scale map. This shader used to exponentiate the RAW decoder
    // log-scale, applying neither the bias nor the ceiling; the defaults below
    // reproduce that exactly, so an undeclared bundle is unchanged.
    scaleLogBias = 0,
    scaleLogMax = Infinity,
    sigmaMax = 0,
    sigmaOffset = 0,
  ) {
    this.renderer = renderer;
    this.d = dims;
    this.numSplats = sampleMap ? sampleMap.length : dims.HW;
    if (sampleMap) { this.texSampleMap = dataTex(sampleMap.length); writeTex(this.texSampleMap, sampleMap); }
    this.scaleMax = scaleMax;
    this.triKappa = triKappa;
    this.sliverQMin = sliverQMin;
    this.scaleMap = scaleMapConstants({ sigmaMax, sigmaOffset, scaleLogBias, scaleLogMax });
    this.shK = shK;
    this.branch = branch;
    // gpuJac: compute the per-face Jacobian inline in pass1 from verts + a static
    // faces texture, dropping the ~F*9-float per-frame jac upload (the dominant
    // lift-stage cost on mobile) + the CPU computeJacobians. Needs mesh.faces.
    this.gpuJac = gpuJac && !!mesh.faces;
    this.p1w = TW; this.p1h = Math.ceil(dims.HW / TW);
    if (untileSrc instanceof ThreeUntile) { this.untile = untileSrc; this.ownsUntile = false; }
    // Duck-typed static source ({head,body}{Geom,Color} textures + width) — a
    // caller with frozen geom/color data textures instead of a live NV12 untile.
    // Caller owns/disposes it.
    else if (untileSrc && (untileSrc as { headGeom?: THREE.Texture }).headGeom?.isTexture) {
      this.untile = untileSrc as unknown as ThreeUntile; this.ownsUntile = false;
    }
    else if (untileSrc) { this.untile = new ThreeUntile(renderer, untileSrc as UntileMeta); this.ownsUntile = true; }
    // geom/color always come from the untile (shared or own-meta); there is no
    // CPU-upload path in the live streaming client.
    if (!this.untile) throw new Error("StreamLiftGenerator requires an untile source (shared ThreeUntile or UntileMeta)");

    // statics (uploaded once)
    this.texIdxim = dataTex(dims.HW * 3); writeTex(this.texIdxim, mesh.idxim);
    this.texBarim = dataTex(dims.HW * 3); writeTex(this.texBarim, mesh.barim);
    this.texTriim = dataTex(dims.HW);     writeTex(this.texTriim, mesh.triim);
    this.texValid = dataTex(dims.HW);     writeTex(this.texValid, mesh.valid);
    // per-pose (filled by setPose)
    this.texVerts = dataTex(dims.V * 3);
    this.texJac = dataTex(dims.F * 9);
    this.texFaceValid = dataTex(dims.F);
    if (this.gpuJac) { this.texFaces = dataTex(dims.F * 3); writeTex(this.texFaces, mesh.faces!); }

    // pass1 MRT target (3 × RGBA32F)
    this.rt = new THREE.WebGLRenderTarget(this.p1w, this.p1h, {
      count: 3, type: THREE.FloatType, format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
      depthBuffer: false, stencilBuffer: false, generateMipmaps: false,
    });
    for (const t of this.rt.textures) { t.wrapS = THREE.ClampToEdgeWrapping; t.wrapT = THREE.ClampToEdgeWrapping; }

    // gpuJac → compile the inline-Jacobian variant of pass1 (#define GPUJAC 1,
    // after three's injected #version) reading facesTex; else the jac/faceValid
    // upload variant (byte-identical to the original — parity preserved).
    const jacUniforms: Record<string, THREE.IUniform> = this.gpuJac
      ? { facesTex: { value: this.texFaces }, twFaces: { value: (this.texFaces as any)._tw } }
      : { jacTex: { value: this.texJac }, faceValidTex: { value: this.texFaceValid },
          twJac: { value: (this.texJac as any)._tw }, twFaceValid: { value: (this.texFaceValid as any)._tw } };
    this.fsMat = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: FULLSCREEN_VS.replace("#version 300 es\n", ""), // three prepends the directive
      fragmentShader: "#define GPUJAC " + (this.gpuJac ? "1" : "0") + "\n" + PASS1_FS.replace("#version 300 es\n", ""),
      depthTest: false, depthWrite: false,
      uniforms: {
        geomTex: { value: branch === "body" ? this.untile.bodyGeom : this.untile.headGeom }, vertsTex: { value: this.texVerts },
        ...jacUniforms,
        idximTex: { value: this.texIdxim }, barimTex: { value: this.texBarim },
        triimTex: { value: this.texTriim }, validTex: { value: this.texValid },
        twGeom: { value: this.untile.width }, twVerts: { value: (this.texVerts as any)._tw },
        nVerts: { value: 0 },
        twIdxim: { value: (this.texIdxim as any)._tw }, twBarim: { value: (this.texBarim as any)._tw },
        twTriim: { value: (this.texTriim as any)._tw }, twValid: { value: (this.texValid as any)._tw },
        H: { value: dims.H }, W: { value: dims.W }, HW: { value: dims.HW }, outW: { value: this.p1w },
        // Triangle-bounded clamp. Both default 0 = OFF, so a bundle exported
        // from a checkpoint trained without --tri_size_cap renders exactly as
        // before. Set from the scene manifest, which carries the values the
        // checkpoint was TRAINED with -- a web render at a different kappa is a
        // different model, the same way rendering at a different --clothes_uv_erode
        // paints splats that were never supervised.
        uTriKappa: { value: this.triKappa ?? 0 },
        uSliverQMin: { value: this.sliverQMin ?? 0 },
        // A GLSL float has no Infinity literal, so an absent ceiling becomes a
        // value no log-scale reaches (exp(88) is already f32 max).
        uBounded: { value: this.scaleMap.bounded },
        uLogSigmaMax: { value: this.scaleMap.logSigmaMax },
        uScaleOffset: { value: this.scaleMap.offset },
        uScaleLogMax: { value: Number.isFinite(this.scaleMap.scaleLogMax)
          ? this.scaleMap.scaleLogMax : 1e30 },
      },
    });
    // 3-vertex fullscreen triangle; the vertex shader uses gl_VertexID so the
    // position values are irrelevant, but three needs an attribute to set the
    // draw count.
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(9), 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9); // never frustum-cull
    this.fsScene.add(new THREE.Mesh(geo, this.fsMat));

    // generator (pass2 dyno)
    const C = (v: number) => dynoConst("int", v);
    this.generator = new SplatGenerator({
      numSplats: this.numSplats,
      generator: dynoBlock(
        { index: "int" }, { gsplat: Gsplat },
        ({ index }: any) => {
          const o = liftDyno({
            index,
            o0: dynoSampler2D(this.rt.textures[0], "p1o0"),
            o1: dynoSampler2D(this.rt.textures[1], "p1o1"),
            o2: dynoSampler2D(this.rt.textures[2], "p1o2"),
            validTex: dynoSampler2D(this.texValid, "p1valid"),
            colorTex: dynoSampler2D(branch === "body" ? this.untile.bodyColor : this.untile.headColor, "p1color"),
            H: C(dims.H), W: C(dims.W), N: C(dims.N), HW: C(dims.HW),
            twP: C(this.p1w), twV: C((this.texValid as any)._tw),
            twC: C(this.untile.width),
            ...(this.shK > 0 ? { camPos: this.camPos, K: C(this.shK) } : {}),
            ...(this.texSampleMap ? { sampleTex: dynoSampler2D(this.texSampleMap, "p2smp"), twS: C((this.texSampleMap as any)._tw) } : {}),
            ...(this.scaleMax > 0 ? { scaleMax: dynoConst("float", this.scaleMax) } : {}),
          }, this.shK > 0, !!this.texSampleMap).outputs;
          return {
            gsplat: combineGsplat({
              flags: o.flags, index,
              center: o.center, scales: o.scales, quaternion: o.quaternion,
              rgb: o.rgb, opacity: o.opacity,
            }),
          };
        },
      ),
      update: () => { /* version bumped explicitly in runPass1Bump() */ },
    });
  }

  /** SH mode: set the camera world position each render frame (object space =
   *  world for the identity-transform generator). No-op when SH is off. */
  setCamera(pos: THREE.Vector3): void {
    if (this.shK > 0) (this.camPos as any).value.copy(pos);
  }

  /** Per-pose: upload verts (+ CPU jacobians/faceValid when gpuJac is off).
   *  Call before runLift for a new pose. */
  setPose(verts: ArrayLike<number>, jacobians?: ArrayLike<number>, faceValid?: ArrayLike<number>): void {
    writeTex(this.texVerts, verts);
    // The shader needs the vertex COUNT, not the texture width, to reproduce
    // torch's negative-index wrap on the -1 that `idxim` carries outside every
    // UV chart. See liftGlsl.ts's vert().
    this.fsMat.uniforms.nVerts.value = (verts.length / 3) | 0;
    // gpuJac computes the Jacobian inline in pass1 → no jac/faceValid upload.
    if (!this.gpuJac) { writeTex(this.texJac, jacobians!); writeTex(this.texFaceValid, faceValid!); }
  }

  /** Point pass1's vertsTex at an EXTERNAL texture (e.g. a GPU-skinning render
   *  target) of data-texture width `tw`. setPose then no longer affects verts —
   *  the external pass owns them. */
  setVertsTexture(tex: THREE.Texture, tw: number, nVerts?: number): void {
    this.fsMat.uniforms.vertsTex.value = tex;
    this.fsMat.uniforms.twVerts.value = tw;
    // An external verts texture bypasses setPose, so the wrap count has to
    // arrive with it. Left unchanged when the caller does not know it, which
    // keeps the old two-argument call sites compiling -- but a caller that
    // owns the vertices should pass it, or chart-edge texels sample the wrong
    // base position.
    if (nVerts !== undefined) this.fsMat.uniforms.nVerts.value = nVerts | 0;
  }

  private runPass1Bump(): void {
    const prevRt = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(this.rt);
    this.renderer.render(this.fsScene, this.fsCam);   // pass1 reads this branch's untile buffers
    this.renderer.setRenderTarget(prevRt);
    this.generator.updateVersion();
  }

  /** SHARED-untile mode: the caller already ran the shared untile this frame; this
   *  branch just runs its pass1 (reading its head/body buffers) + marks dirty. */
  runLift(): void {
    if (!this.untile) throw new Error("StreamLiftGenerator: runLift needs a shared untile");
    this.runPass1Bump();
  }

  /** Debug: the pass1 MRT (textures[0..2] = pos+c00 / c01,c02,c11,c12 / c22,op). */
  get pass1RT(): THREE.WebGLRenderTarget { return this.rt; }
  get pass1Size(): [number, number] { return [this.p1w, this.p1h]; }

  dispose(): void {
    this.rt.dispose();
    if (this.ownsUntile) this.untile?.dispose();
    for (const t of [this.texIdxim, this.texBarim, this.texTriim, this.texValid,
      this.texVerts, this.texJac, this.texFaceValid, this.texFaces, this.texSampleMap]) t?.dispose();
    this.fsScene.traverse((o) => (o as THREE.Mesh).geometry?.dispose?.());
    this.fsMat.dispose();
  }
}
